import merge from "lodash/merge";
import {DISPOSITIONS_BY_NAME} from "models/options";
import {getEntityTypeIcon} from "utils/icons";
import {objectMap} from "utils/misc";
import theme from "theme/index.scss";
import {
  COMBINE_FIELDS,
  COMBO_STYLES,
  EDGE_STYLES,
  NODE_STYLES,
  THEME_COLORS
} from "./graphStyleConstants";
import {
  getCombinedOnTuple,
  getComboLabel,
  getComboType,
  isEdge,
  isNode
} from "./graphUtils";

/**
 *
 * Style all 'items' given the value of 'currentTheme'.
 */
export const setNodeStyle = (items, currentTheme) =>
  objectMap(items, (item) => setItemStyle(item, currentTheme));

/**
 * Given a Node object, attach styling properties and return the node object.
 *
 * @todo Currently, this just attaches static styles based on `theme` value.
 *   In future, this will need more logic to dynamically asign styling based on disposition
 *
 * @param {Object} item Node Object
 * @param {String} currentTheme current theme.
 * @returns {Object} Node Style object.
 */
const setItemStyle = (item, currentTheme) =>
  merge(
    {},
    item,
    isNode(item) ? NODE_STYLES[currentTheme] : EDGE_STYLES[currentTheme]
  );

/**
 * Given an node and theme, return color to render node.
 *
 * @param {Object} node
 * @param {String} currentTheme Color map to source colors from.
 * @returns Hex color value
 */
const getNodeColor = (node, currentTheme) => {
  const colorMap = isDefaultTheme(currentTheme)
    ? THEME_COLORS.LIGHT
    : THEME_COLORS.DARK;

  if (!node) {
    return colorMap.unknown;
  }

  if (isTargetOrAssetType(node.type) || isTargetOrAssetGroupType(node.type)) {
    return colorMap.asset;
  }

  // @todo This will always resolve to "Unknown" since node dispositions
  // are not available yet. Revisit this logic when it's available.
  let dispositionName =
    node.disposition_name || DISPOSITIONS_BY_NAME.Unknown.title;
  dispositionName = dispositionName.toLowerCase();

  return colorMap[dispositionName] || colorMap.unknown;
};

const getComboNodeColor = (comboNodeData, combinedOnType, currentTheme) => {
  const colorMap = isDefaultTheme(currentTheme)
    ? THEME_COLORS.LIGHT
    : THEME_COLORS.DARK;

  // If we have 'combinedOnType' and know it's asset (target) related, we will
  // assert it's style directly from this.
  if (
    combinedOnType === COMBINE_FIELDS.targetGroup || // 1. Is Target-Group
    combinedOnType === COMBINE_FIELDS.observableOfTarget || // 2. Is Observable-ogf-Target group
    (combinedOnType === COMBINE_FIELDS.simplification && // 3. Is via simplification rules AND has targets list.
      comboNodeData.targets?.length)
  ) {
    return colorMap.asset;
  }

  // Otherwise use comboNode data to assert it's color, e.g. disposition color.
  if (comboNodeData) {
    return getNodeColor(comboNodeData, currentTheme);
  }

  // It's possible comboNode is undefined and combinedOnType is not matched, so fallback to default node color.
  return colorMap.unknown;
};

/**
 * Called by onCombineNodes.
 *
 * Given set of params describing a combo (group),
 * returns a styling object.
 *
 * NOTE: 'Group' is term used by API data, 'Combo' is the concept of a
 * "grouped node". We'll use 'combo' when referring to graph data for
 * consistency.
 *
 * @param {String} id Unique internal ID of combo.
 * @param {Object} nodes Key/Value pairs. Lookup for nodes comtained by combo
 * @param {Object} combo key/value of COMBINE_ON/Combo_key.
 * @param {Object} graphGroups All groups (combos) lookup key/value pairs.
 * @param {Object} openCombos All Open groups(combos) lookup.
 * @param {String} currentTheme
 * @returns {Object} Combo style object.
 */
export const getComboStyle = (
  id,
  nodes,
  combo,
  graphGroups,
  openCombos,
  currentTheme
) => {
  const nodeCount = Object.values(nodes).length;
  const [combinedOnType, combinedOnKey] = getCombinedOnTuple(combo);

  const groupItem = graphGroups[combinedOnKey];
  const comboType = getComboType(groupItem);
  const comboLabel = getComboLabel(groupItem, combinedOnType);
  const comboColor = getComboNodeColor(
    groupItem?.data,
    combinedOnType,
    currentTheme
  );

  const groupStyle = merge({}, COMBO_STYLES[currentTheme], {
    open: !!openCombos[id],
    label: {
      text: comboLabel
    },
    border: {
      color: comboColor
    },
    glyphs: [
      {
        label: {
          text: nodeCount
        }
      }
    ],
    closedStyle: {
      fontIcon: {
        text: `ph-${getEntityTypeIcon(comboType)}`,
        color: comboColor
      },
      border: {
        color: comboColor
      }
    }
  });

  return groupStyle;
};

/**
 * Standard handle function for Item Interaction.
 *
 * Add prop to Graph as follows:
 * <Chart {...chartProps} onItemInteraction={handleItemInteraction} />
 *
 * Currently, it's only action is to show Edge labels on hover or selection of an edge.
 * Extend as needed. Can also handle nodes, combos, etc.
 */
export const handleItemInteraction = ({
  id,
  item,
  hovered,
  selected,
  setStyle
}) => {
  if (isEdge(item) && (hovered || selected)) {
    setStyle({
      [id]: {
        color: theme.graphCiscoBlue,
        label: {
          text: item.labelText
        }
      }
    });
  }
};

const isTargetOrAssetType = (type) => ["target", "asset"].includes(type);

const isTargetOrAssetGroupType = (type) =>
  ["target-group", "asset-group"].includes(type);

const isDefaultTheme = (theme) => theme === "default";
